import { withRefreshOn401 } from "@saas-on-sheets/auth-client";

export interface HelloAppClientConfig {
  appApiUrl: string;
  authApiUrl: string;
}

export interface InitResult {
  sheetId: string;
  sheetUrl: string;
  driveFolderId: string;
  driveFolderUrl: string;
}

export interface FilePayload {
  name: string;
  mimeType: string;
  contentBase64: string;
}

export interface SubmitInput {
  name: string;
  message: string;
  file?: FilePayload;
}

export interface SubmitResult {
  updatedRange: string;
  updatedRows: number;
  submittedAt: string;
  fileId: string;
  fileName: string;
  fileUrl: string;
}

export interface SubmissionRow {
  submittedAt: string;
  name: string;
  message: string;
  fileId: string;
  fileName: string;
  fileUrl: string;
}

export interface ListResult {
  rows: SubmissionRow[];
  sheetUrl: string;
  driveFolderUrl: string;
}

export interface HelloAppClient {
  init(): Promise<InitResult>;
  submit(input: SubmitInput): Promise<SubmitResult>;
  list(): Promise<ListResult>;
}

// SSO: when the server returns 401/403 with a structured `{error: ...}` body,
// we expose it as a typed error so the dashboard can switch UI state
// (e.g. show a "buy a license" panel when reason === 'no_license_for_app').
export class HelloAppApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly reason: string,
  ) {
    super(`${status} ${reason}`);
    this.name = "HelloAppApiError";
  }
}

async function parseOrThrow<T>(res: Response, label: string): Promise<T> {
  if (!res.ok) {
    let reason = "";
    try {
      const body = (await res.clone().json()) as { error?: string };
      reason = body.error ?? "";
    } catch {
      // Non-JSON body (e.g. Cloudflare edge block HTML page). NEVER use the
      // raw text as `reason` — it flows verbatim into the dashboard's
      // "No access" panel. Collapse to a stable code instead.
      reason = "upstream_blocked";
    }
    if (res.status === 401 || res.status === 403) {
      throw new HelloAppApiError(res.status, reason || "unauthorized");
    }
    throw new Error(`${label} failed (${res.status}): ${reason}`);
  }
  return (await res.json()) as T;
}

export function createHelloAppClient(config: HelloAppClientConfig): HelloAppClient {
  // Empty appApiUrl means "use relative /api/*" — handled by Pages Function
  // proxy in production and Vite proxy in local dev.
  const base = config.appApiUrl ? config.appApiUrl.replace(/\/$/, "") : "";
  const refreshCfg = { authApiUrl: config.authApiUrl };

  return {
    async init() {
      const res = await withRefreshOn401(refreshCfg, () =>
        fetch(`${base}/api/init`, {
          method: "POST",
          credentials: "include",
          cache: "no-store",
        }),
      );
      return parseOrThrow<InitResult>(res, "init");
    },

    async submit(input) {
      const res = await withRefreshOn401(refreshCfg, () =>
        fetch(`${base}/api/submit`, {
          method: "POST",
          credentials: "include",
          cache: "no-store",
          headers: {
            "content-type": "application/json",
            "idempotency-key": crypto.randomUUID(),
          },
          body: JSON.stringify(input),
        }),
      );
      return parseOrThrow<SubmitResult>(res, "submit");
    },

    async list() {
      const res = await withRefreshOn401(refreshCfg, () =>
        fetch(`${base}/api/list`, { credentials: "include", cache: "no-store" }),
      );
      return parseOrThrow<ListResult>(res, "list");
    },
  };
}

export function fileToBase64(file: File): Promise<FilePayload> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // readAsDataURL returns "data:<type>;base64,<content>" — strip the prefix.
      const comma = result.indexOf(",");
      const contentBase64 = comma >= 0 ? result.substring(comma + 1) : result;
      resolve({
        name: file.name,
        mimeType: file.type || "application/octet-stream",
        contentBase64,
      });
    };
    reader.onerror = () => reject(reader.error ?? new Error("FileReader error"));
    reader.readAsDataURL(file);
  });
}
